import React from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_INSULATION,
  GET_ALL_INSULATIONS,
  LINK_INSULATION_JOBS,
  REMOVE_INSULATION,
  UPDATE_INSULATION,
} from '../../../graph-queries/tar/insulation';
import { TInsulation } from '../../../lib/types';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import InsulationForm from '../../organisms/InsulationForm/InsulationForm';
import { ColumnDef } from '@tanstack/react-table';
import { authorCell, useAlertText } from '../../organisms/AspectsTable/util';
import { useSelectedAsset } from '../../../lib/util';
import { useHasTarAdminAccess, useHasUserAccess } from '../../../lib/util/auth';
import {
  INSULATION_HEAT_TRACING_ENUM,
  INSULATION_MATERIALS_ENUM,
  INSULATION_STATUS_ENUM,
} from '../../../lib/constants/dbConstants';

export const columns: ColumnDef<TInsulation>[] = [
  {
    header: 'Description',
    accessorKey: 'description',
    enableSorting: false,
  },
  {
    header: 'Plan Reference',
    accessorKey: 'referenceNumber',
    enableSorting: false,
  },
  {
    header: 'Circuit Number ',
    accessorKey: 'circuitNumber',
    enableSorting: false,
  },
  {
    header: 'Heat Pack',
    accessorKey: 'heatTracing',
    enableSorting: false,
    accessorFn: (row) => INSULATION_HEAT_TRACING_ENUM[row.heatTracing],
  },
  {
    header: 'Materials',
    accessorKey: 'materials',
    enableSorting: false,
    accessorFn: (row) => INSULATION_MATERIALS_ENUM[row.materials],
  },
  {
    header: 'Status',
    accessorKey: 'status',
    enableSorting: false,
    accessorFn: (row) => INSULATION_STATUS_ENUM[row.status],
  },
  {
    header: 'Comments',
    accessorKey: 'comments',
    enableSorting: false,
  },
  {
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: authorCell,
  },
];

const InsulationsPage: React.FC = () => {
  const { alertText, setAlertText } = useAlertText();
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const asset = useSelectedAsset();
  const { data, loading } = useQuery(GET_ALL_INSULATIONS, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });
  const [createInsulation, { loading: isCreating }] = useMutation(CREATE_INSULATION, {
    refetchQueries: [GET_ALL_INSULATIONS],
    awaitRefetchQueries: true,
  });
  const [updateInsulation, { loading: isUpdating }] = useMutation(UPDATE_INSULATION, {
    refetchQueries: [GET_ALL_INSULATIONS],
    awaitRefetchQueries: true,
  });
  const [linkInsulationJobs, { loading: isLinking }] = useMutation(LINK_INSULATION_JOBS, {
    refetchQueries: [GET_ALL_INSULATIONS],
    awaitRefetchQueries: true,
  });
  const [removeInsulation, { loading: isRemoving }] = useMutation(REMOVE_INSULATION, {
    refetchQueries: [GET_ALL_INSULATIONS],
    awaitRefetchQueries: true,
  });

  const onAddSubmit = (insulation: TInsulation) => {
    const { insulationId, updatedBy, updatedAt, tarJobs, ...input } = insulation;
    return createInsulation({ variables: { input } }).then(() =>
      setAlertText('Insulation successfully added')
    );
  };
  const onEditSubmit = (insulation: TInsulation) => {
    const { updatedBy, updatedAt, tarJobs, ...input } = insulation;
    return updateInsulation({ variables: { input } }).then(() =>
      setAlertText('Insulation successfully updated')
    );
  };

  const onLinkSubmit = (selection: TInsulation[], jobIds: number[]) => {
    return Promise.all(
      selection.map((insulation) =>
        linkInsulationJobs({
          variables: {
            insulationId: insulation.insulationId,
            jobIds: jobIds,
          },
        })
      )
    ).then(() =>
      setAlertText(
        `Insulation successfully linked to ${jobIds.length} job${jobIds.length > 1 ? 's' : ''}`
      )
    );
  };

  const onRemoveSubmit = (selection: TInsulation[]) => {
    return Promise.all(
      selection.map((insulation) =>
        removeInsulation({
          variables: { insulationId: insulation.insulationId },
        })
      )
    ).then(() => setAlertText(`Insulation successfully deleted`));
  };

  const isLoading = isCreating || isUpdating || isLinking || isRemoving || loading || !!alertText;
  return (
    <Layout>
      <Container>
        {loading ? (
          'Loading...'
        ) : (
          <AspectsTable<TInsulation>
            hideSelectionColumn
            aspectName="Insulation"
            data={(data?.allInsulations as unknown as TInsulation[]) || []}
            onLinkSubmit={onLinkSubmit}
            onRemoveSubmit={onRemoveSubmit}
            columns={columns}
            alertText={alertText}
            renderAddForm={(onClose) => (
              <InsulationForm
                onSubmit={onAddSubmit}
                onClose={onClose}
                type={'add'}
                isLoading={isLoading}
              />
            )}
            renderEditForm={(onClose, defaultValue) => (
              <InsulationForm
                defaultValues={defaultValue}
                onSubmit={onEditSubmit}
                onClose={onClose}
                type={'edit'}
                isLoading={isLoading}
              />
            )}
            creationDisabled={!hasUserAccess}
            getRowMutationDisabled={(_row) => !hasUserAccess}
            getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
            searchEnabled={true}
            searchPlaceholderText="Search the insulation tracker..."
            isLoading={isLoading}
          />
        )}
      </Container>
    </Layout>
  );
};

export default InsulationsPage;
